import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart3, Brain, Play, Sparkles, ThumbsUp, ThumbsDown, Check, X, Trash2, Plus, ArrowLeft, Save } from 'lucide-react';
import { withAuthHeaders } from '../utils/auth';

const API = process.env.REACT_APP_BACKEND_URL;

// Палитра графика проверена валидатором (dark, поверхность --bg-card)
const SERIES = [
  { key: 'ai_resolved', label: 'Решено AI', color: '#0f9e90' },
  { key: 'other', label: 'Остальные', color: '#8b7cf6' },
];

const OUTCOME_LABELS = {
  ai_resolved: '🤖 Решено AI',
  manager_resolved: '👨‍💼 Решено менеджером',
  unresolved: '⚠️ Не решено',
  escalated: '🔥 Ждёт менеджера',
  open: '💬 В работе',
  abandoned: '💤 Без ответа',
  archived: '📦 Архив (импорт)',
};

const LESSON_STATUS = {
  pending: { label: 'На проверке', badge: 'warning' },
  approved: { label: 'Применён', badge: 'success' },
  rejected: { label: 'Отклонён', badge: 'muted' },
  merged: { label: 'Объединён', badge: 'info' },
};

const TYPE_LABELS = { prompt_rule: 'Правило AI', kb_article: 'Новая статья базы знаний', kb_update: 'Правка существующей статьи' };

const CATEGORIES = [
  ['подключение', 'Подключение'], ['оплата', 'Оплата'], ['подписка', 'Подписка и тарифы'],
  ['устройства', 'Устройства'], ['трафик', 'Трафик'], ['аккаунт', 'Аккаунт'], ['другое', 'Общие'],
];
const CATEGORY_LABELS = Object.fromEntries(CATEGORIES);

function CategorySelect({ value, onChange, style }) {
  return (
    <select className="select" value={value || ''} onChange={e => onChange(e.target.value)} style={style}>
      <option value="">Тема — определить автоматически</option>
      {CATEGORIES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
    </select>
  );
}

const pct = v => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`);
const stripHtml = s => (s || '').replace(/<[^>]+>/g, '');
const formatDate = d => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

function useApi(auth) {
  return useCallback(async (path, opts = {}) => {
    const r = await fetch(`${API}/api/learning${path}`, {
      ...opts,
      headers: withAuthHeaders(auth, { 'Content-Type': 'application/json' }),
    });
    return r.json();
  }, [auth]);
}

function StatTile({ label, value, hint }) {
  return (
    <div className="card" style={{ padding: 12, marginBottom: 0 }}>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{label}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>{value}</div>
      {hint && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function DailyChart({ daily }) {
  const [hover, setHover] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const rows = daily.map(d => ({ ...d, other: Math.max(0, d.created - d.ai_resolved) }));
  const max = Math.max(1, ...rows.map(d => d.created));
  const H = 120;

  return (
    <div className="card">
      <div className="card-header" style={{ justifyContent: 'space-between' }}>
        <span className="card-title">Тикеты по дням</span>
        <button className="btn btn-secondary btn-sm" onClick={() => setShowTable(!showTable)}>
          {showTable ? 'График' : 'Таблица'}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 14, fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
        {SERIES.map(s => (
          <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />{s.label}
          </span>
        ))}
      </div>

      {showTable ? (
        <div style={{ maxHeight: 220, overflowY: 'auto', fontSize: '0.78rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: 'var(--text-secondary)', textAlign: 'left' }}>
              <th>Дата</th><th>Создано</th><th>Решено AI</th><th>Эскалаций</th><th>Закрыто</th>
            </tr></thead>
            <tbody>{rows.slice().reverse().map(d => (
              <tr key={d.date}><td>{d.date}</td><td>{d.created}</td><td>{d.ai_resolved}</td><td>{d.escalated}</td><td>{d.closed}</td></tr>
            ))}</tbody>
          </table>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: H, borderBottom: '1px solid var(--border-default)' }}
               onMouseLeave={() => setHover(null)}>
            {rows.map((d, i) => (
              <div key={d.date} onMouseEnter={() => setHover(i)}
                   style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', cursor: 'default' }}>
                {d.other > 0 && <div style={{ height: (d.other / max) * H, background: SERIES[1].color, borderRadius: '4px 4px 0 0', marginBottom: d.ai_resolved ? 2 : 0, opacity: hover === null || hover === i ? 1 : 0.5 }} />}
                {d.ai_resolved > 0 && <div style={{ height: (d.ai_resolved / max) * H, background: SERIES[0].color, borderRadius: d.other ? 0 : '4px 4px 0 0', opacity: hover === null || hover === i ? 1 : 0.5 }} />}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 4 }}>
            <span>{rows[0]?.date}</span><span>макс. {max} в день</span><span>{rows[rows.length - 1]?.date}</span>
          </div>
          {hover !== null && rows[hover] && (
            <div style={{ position: 'absolute', top: 0, left: `${Math.min(70, (hover / rows.length) * 100)}%`, background: 'var(--bg-elevated)', border: '1px solid var(--border-hover)', borderRadius: 8, padding: '6px 10px', fontSize: '0.75rem', pointerEvents: 'none' }}>
              <div style={{ fontWeight: 600 }}>{rows[hover].date}</div>
              <div>Создано: {rows[hover].created}</div>
              <div>Решено AI: {rows[hover].ai_resolved}</div>
              <div>Эскалаций: {rows[hover].escalated}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BreakdownList({ title, items }) {
  const total = items.reduce((s, [, n]) => s + n, 0);
  if (!items.length) return null;
  return (
    <div className="card">
      <div className="card-header"><span className="card-title">{title}</span></div>
      {items.map(([label, n]) => (
        <div key={label} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
            <span>{label}</span><span style={{ color: 'var(--text-secondary)' }}>{n}</span>
          </div>
          <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 4, marginTop: 3 }}>
            <div style={{ width: `${total ? (n / total) * 100 : 0}%`, height: '100%', background: SERIES[0].color, borderRadius: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
}


const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const periodLabel = (key, bucket) => {
  if (bucket === 'month') { const [y, m] = key.split('-'); return `${MONTHS[+m - 1]} ${y.slice(2)}`; }
  const d = new Date(key); return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Линейный график одной метрики. Пустые периоды — разрыв линии; мало данных (<5) — полая точка.
function MetricLine({ rows, valueKey, nKey, domain, format, title, startIndex, bucket }) {
  const [hover, setHover] = useState(null);
  const W = 600, H = 120, P = { l: 34, r: 10, t: 10, b: 8 };
  const n = rows.length;
  const x = i => P.l + (n === 1 ? (W - P.l - P.r) / 2 : (i * (W - P.l - P.r)) / (n - 1));
  const y = v => P.t + (1 - (v - domain[0]) / (domain[1] - domain[0])) * (H - P.t - P.b);
  const segments = [];
  let cur = [];
  rows.forEach((r, i) => {
    if (r[valueKey] === null || r[valueKey] === undefined) { if (cur.length) segments.push(cur); cur = []; }
    else cur.push([x(i), y(r[valueKey])]);
  });
  if (cur.length) segments.push(cur);
  const ticks = [domain[0], (domain[0] + domain[1]) / 2, domain[1]];
  const color = SERIES[0].color;

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 4 }}>{title}</div>
      <div style={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
          {ticks.map(t => (
            <g key={t}>
              <line x1={P.l} x2={W - P.r} y1={y(t)} y2={y(t)} stroke="var(--border-default)" strokeWidth="1" />
              <text x={P.l - 6} y={y(t) + 4} textAnchor="end" fontSize="11" fill="var(--text-muted)">{format(t)}</text>
            </g>
          ))}
          {startIndex !== null && startIndex >= 0 && (
            <g>
              <line x1={x(startIndex)} x2={x(startIndex)} y1={P.t} y2={H - P.b} stroke="var(--text-muted)" strokeDasharray="4 4" strokeWidth="1" />
            </g>
          )}
          {segments.map((seg, i) => (
            <polyline key={i} points={seg.map(p => p.join(',')).join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          ))}
          {rows.map((r, i) => r[valueKey] === null || r[valueKey] === undefined ? null : (
            <circle key={i} cx={x(i)} cy={y(r[valueKey])} r={hover === i ? 5 : 4}
                    fill={r[nKey] < 5 ? 'var(--bg-card)' : color} stroke={color} strokeWidth="2" />
          ))}
          {rows.map((r, i) => (
            <rect key={`h${i}`} x={x(i) - (W - P.l - P.r) / Math.max(1, n - 1) / 2} y={0}
                  width={(W - P.l - P.r) / Math.max(1, n - 1)} height={H} fill="transparent"
                  onMouseEnter={() => setHover(i)} />
          ))}
        </svg>
        {hover !== null && rows[hover] && (
          <div style={{ position: 'absolute', top: 0, left: `${Math.min(68, Math.max(0, (x(hover) / W) * 100 - 10))}%`, background: 'var(--bg-elevated)', border: '1px solid var(--border-hover)', borderRadius: 8, padding: '6px 10px', fontSize: '0.74rem', pointerEvents: 'none', whiteSpace: 'nowrap' }}>
            <div style={{ fontWeight: 600 }}>{periodLabel(rows[hover].period, bucket)}</div>
            <div>{title}: {rows[hover][valueKey] === null || rows[hover][valueKey] === undefined ? 'нет данных' : format(rows[hover][valueKey])}</div>
            <div style={{ color: 'var(--text-muted)' }}>по {rows[hover][nKey]} тикетам{rows[hover].archived ? ` · архив ${rows[hover].archived}` : ''}</div>
            {rows[hover].lessons_applied > 0 && <div>Применено уроков: +{rows[hover].lessons_applied}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressCard({ api }) {
  const [bucket, setBucket] = useState('month');
  const [data, setData] = useState(null);
  const [table, setTable] = useState(false);
  useEffect(() => { setData(null); api(`/progress?bucket=${bucket}&periods=${bucket === 'week' ? 16 : 12}`).then(setData); }, [api, bucket]);

  if (!data) return <div className="card"><div className="loading-spinner" /></div>;
  const rows = data.periods;
  const startKey = data.learning_start ? (bucket === 'month' ? data.learning_start.slice(0, 7) : null) : null;
  let startIndex = null;
  if (data.learning_start) {
    const ts = new Date(data.learning_start).getTime();
    startIndex = rows.findIndex(r => (bucket === 'month' ? r.period === startKey : new Date(r.period).getTime() + 7 * 864e5 > ts));
  }
  const b = data.before, a = data.after;
  const delta = (x1, x0) => (x1 === null || x0 === null ? null : x1 - x0);
  const dRate = delta(a.success_rate, b.success_rate);
  const dQ = delta(a.avg_quality, b.avg_quality);
  const fewAfter = a.judged < 20;

  return (
    <div className="card">
      <div className="card-header" style={{ justifyContent: 'space-between' }}>
        <span className="card-title"><Brain size={16} style={{ marginRight: 6 }} />Прогресс обучения</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className={`filter-tab ${bucket === 'month' ? 'active' : ''}`} onClick={() => setBucket('month')}>Месяцы</button>
          <button className={`filter-tab ${bucket === 'week' ? 'active' : ''}`} onClick={() => setBucket('week')}>Недели</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setTable(!table)}>{table ? 'Графики' : 'Таблица'}</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 12 }}>
        <StatTile label="До обучения: решено AI" value={pct(b.success_rate)} hint={`${b.judged} тикетов · качество ${b.avg_quality ?? '—'}`} />
        <StatTile label="После: решено AI" value={pct(a.success_rate)}
                  hint={`${a.judged} тикетов · качество ${a.avg_quality ?? '—'}${dRate !== null ? ` · ${dRate >= 0 ? '+' : ''}${Math.round(dRate * 100)} п.п.` : ''}${dQ !== null ? ` / ${dQ >= 0 ? '+' : ''}${dQ.toFixed(2)}` : ''}`} />
        <StatTile label="Применено уроков" value={data.lessons_applied_total}
                  hint={data.learning_start ? `с ${new Date(data.learning_start).toLocaleDateString('ru-RU')}` : 'ещё не начато'} />
      </div>
      {fewAfter && (
        <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginBottom: 10 }}>
          После старта обучения пока мало тикетов ({a.judged}) — сравнение станет надёжным примерно с 20–30. «До» — в основном архив, его исход оценён AI-разбором.
        </div>
      )}

      {table ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.76rem', borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: 'var(--text-secondary)', textAlign: 'left' }}>
              <th>Период</th><th>Тикетов</th><th>Оценено</th><th>Решено AI</th><th>Качество</th><th>Уроков +</th>
            </tr></thead>
            <tbody>{rows.slice().reverse().map(r => (
              <tr key={r.period} style={{ borderTop: '1px solid var(--border-default)' }}>
                <td>{periodLabel(r.period, bucket)}</td><td>{r.tickets}</td><td>{r.judged}</td>
                <td>{pct(r.success_rate)}</td><td>{r.avg_quality ?? '—'}</td><td>{r.lessons_applied || ''}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : (
        <>
          <MetricLine rows={rows} valueKey="success_rate" nKey="judged" domain={[0, 1]} format={v => `${Math.round(v * 100)}%`}
                      title="Доля решённых AI без менеджера" startIndex={startIndex} bucket={bucket} />
          <MetricLine rows={rows} valueKey="avg_quality" nKey="rated" domain={[1, 5]} format={v => (Number.isInteger(v) ? v : v.toFixed(1))}
                      title="Качество ответов AI (1–5, по разборам)" startIndex={startIndex} bucket={bucket} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: 'var(--text-muted)', paddingLeft: '5.6%' }}>
            {rows.map((r, i) => (
              <span key={r.period} style={{ textAlign: 'center', flex: 1, color: i === startIndex ? 'var(--text-secondary)' : undefined }}>
                {(rows.length <= 12 || i % 2 === 0) ? periodLabel(r.period, bucket) : ''}
                {r.lessons_applied > 0 && <div style={{ color: SERIES[0].color }}>+{r.lessons_applied}</div>}
              </span>
            ))}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 6 }}>
            Пунктир — старт обучения (первый одобренный урок). «+N» — уроков применено в периоде. Полая точка — меньше 5 тикетов, значение шумное.
          </div>
        </>
      )}
    </div>
  );
}

function OverviewTab({ api, onOpenReports }) {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [run, setRun] = useState(null);

  const load = useCallback(async () => setStats(await api(`/stats?days=${days}`)), [api, days]);
  useEffect(() => { load(); }, [load]);

  // Фоновый разбор очереди: при открытии подхватываем идущий прогон и опрашиваем прогресс
  const pollRun = useCallback(async () => {
    const r = await api('/run');
    setRun(r.run);
    return r.run;
  }, [api]);
  useEffect(() => { pollRun(); }, [pollRun]);
  useEffect(() => {
    if (run?.status !== 'running') return undefined;
    const t = setInterval(async () => {
      const cur = await pollRun();
      if (cur?.status !== 'running') { load(); }
    }, 3000);
    return () => clearInterval(t);
  }, [run?.status, pollRun, load]);

  const runNow = async () => {
    setMsg('');
    const r = await api('/run', { method: 'POST' });
    if (r.run) setRun(r.run);
    else setMsg(r.message || r.error || 'Нечего разбирать');
  };
  const running = run?.status === 'running';
  const insights = async () => {
    setBusy('insights'); setMsg('');
    const r = await api('/insights', { method: 'POST', body: JSON.stringify({ days: 14 }) });
    setMsg(r.ok ? `Сводный отчёт готов, новых уроков: ${r.lesson_ids.length}` : (r.error === 'no_analyses' ? 'Пока нет разобранных тикетов' : (r.error || 'Ошибка')));
    setBusy('');
    if (r.ok) onOpenReports();
  };

  if (!stats) return <div className="empty-state"><div className="loading-spinner" /></div>;
  const a = stats.analyses || {};

  return (
    <div>
      <div className="filter-tabs" style={{ marginBottom: 12 }}>
        {[7, 30, 90].map(d => (
          <button key={d} className={`filter-tab ${days === d ? 'active' : ''}`} onClick={() => setDays(d)}>{d} дн.</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 12 }}>
        <StatTile label="Тикетов" value={stats.tickets_total} hint={`за ${stats.period_days} дн.`} />
        <StatTile label="Решено AI без менеджера" value={pct(stats.ai_resolution_rate)} hint={`по ${stats.ai_judged || 0} тикетам с известным исходом`} />
        <StatTile label="Эскалаций" value={pct(stats.escalation_rate)} hint="от всех тикетов" />
        <StatTile label="Качество ответов AI" value={a.avg_ai_quality ? `${a.avg_ai_quality} / 5` : '—'} hint={`по ${a.total || 0} разборам`} />
        <StatTile label="Оценки клиентов" value={`👍 ${stats.ratings.up} · 👎 ${stats.ratings.down}`} />
        <StatTile label="Время до закрытия" value={stats.avg_close_minutes ? `${Math.round(stats.avg_close_minutes)} мин` : '—'} hint="в среднем" />
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title"><Brain size={16} style={{ marginRight: 6 }} />Самообучение</span></div>
        <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 10 }}>
          Ждут разбора: <b>{stats.pending_analysis}</b> · уроков на проверке: <b>{stats.lessons.pending || 0}</b> · применено: <b>{stats.lessons.approved || 0}</b>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={runNow} disabled={!!busy || running || !stats.pending_analysis}>
            <Play size={14} /> {running ? `Разбираю… ${run.processed} из ${run.total}` : `Разобрать все (${stats.pending_analysis})`}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={insights} disabled={!!busy}>
            <Sparkles size={14} /> {busy === 'insights' ? 'Анализирую…' : 'Сводный анализ'}
          </button>
        </div>
        {running && (
          <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 4, marginTop: 10 }}>
            <div style={{ width: `${(run.processed / run.total) * 100}%`, height: '100%', background: SERIES[0].color, borderRadius: 4, transition: 'width 0.5s' }} />
          </div>
        )}
        {run && !running && run.finished_at && (
          <div className="alert alert-info" style={{ marginTop: 10 }}>
            {run.status === 'done' ? `Готово: разобрано ${run.analyzed} тикетов` :
              run.status === 'stopped' ? `Остановлено: разобрано ${run.analyzed}, осталось ${run.left} — AI-провайдер не ответил, повторите позже` :
                `Ошибка разбора: ${run.error || ''}`}
            {run.errors ? ` · ошибок: ${run.errors}` : ''}
          </div>
        )}
        {msg && <div className="alert alert-info" style={{ marginTop: 10 }}>{msg}</div>}
      </div>

      <ProgressCard api={api} />
      <DailyChart daily={stats.daily} />
      <BreakdownList title="Исходы тикетов" items={Object.entries(stats.outcomes).map(([k, n]) => [OUTCOME_LABELS[k] || k, n]).sort((x, y) => y[1] - x[1])} />
      <BreakdownList title="Категории обращений (по разборам AI)" items={a.categories || []} />
      <BreakdownList title="Причины эскалаций" items={stats.escalation_reasons || []} />

      {stats.tools.length > 0 && (
        <div className="card">
          <div className="card-header"><span className="card-title">Инструменты AI</span></div>
          {stats.tools.map(t => (
            <div key={t.name} className="data-row">
              <span className="data-label"><code>{t.name}</code></span>
              <span className="data-value">{t.calls} вызовов{t.failed ? ` · ошибок ${t.failed}` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeedbackBox({ api, ticketId, index, onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [showOld, setShowOld] = useState(false);

  const send = async (verdict, correction, asDraft) => {
    setBusy(true);
    const r = await api(`/tickets/${ticketId}/feedback`, {
      method: 'POST', body: JSON.stringify({ index, verdict, correction, draft: asDraft }),
    });
    setBusy(false);
    if (r.draft) { setDraft(r.draft); return; }
    onDone(r.ok ? 'Оценка сохранена' : (r.detail || 'Ошибка'));
  };

  const apply = async () => {
    setBusy(true);
    const r = await api('/lessons', { method: 'POST', body: JSON.stringify({ ...draft, source_ticket_id: ticketId }) });
    setBusy(false);
    onDone(r.ok ? (draft.type === 'kb_update' ? 'Статья обновлена' : draft.type === 'kb_article' ? 'Статья добавлена в базу знаний' : 'Правило применено') : (r.error || 'Ошибка'));
  };

  const box = { marginTop: 8, flexBasis: '100%', width: '100%' };

  if (draft) {
    return (
      <div style={{ ...box, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: 10 }}>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 6 }}>
          {draft.fallback ? '⚠️ ' : '🧠 '}{draft.reason}
        </div>
        <select className="select" value={draft.type} onChange={e => setDraft({ ...draft, type: e.target.value })} style={{ marginBottom: 6 }}>
          <option value="prompt_rule">Правило для AI</option>
          <option value="kb_article">Новая статья базы знаний</option>
          {draft.article_id && <option value="kb_update">Правка статьи «{draft.title}»{draft.article_pinned ? ' (факт в промпте)' : ''}</option>}
        </select>
        <CategorySelect value={draft.category} onChange={category => setDraft({ ...draft, category })} style={{ marginBottom: 6 }} />
        <input className="input" value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} style={{ marginBottom: 6 }} />
        <textarea className="input" rows={6} value={draft.content} onChange={e => setDraft({ ...draft, content: e.target.value })} />
        {draft.type === 'kb_update' && draft.current_content && (
          <div style={{ fontSize: '0.74rem', marginTop: 6 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowOld(!showOld)}>{showOld ? 'Скрыть' : 'Показать'} текущий текст статьи</button>
            {showOld && <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-muted)', marginTop: 6 }}>{draft.current_content}</div>}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={apply} disabled={busy || !draft.content.trim()}><Check size={12} /> {busy ? 'Применяю…' : 'Применить'}</button>
          <button className="btn btn-secondary btn-sm" onClick={() => onDone('Черновик отменён, оценка «плохо» сохранена')}>Отмена</button>
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <div style={{ ...box, display: 'flex', gap: 6 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => send('good', '', false)} disabled={busy}><ThumbsUp size={12} /> Хорошо</button>
        <button className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}><ThumbsDown size={12} /> Плохо</button>
      </div>
    );
  }
  return (
    <div style={box}>
      <textarea className="input" rows={3}
                placeholder="Что не так? Опишите своими словами: что было на самом деле и как надо было поступить. AI составит из этого правило или правку статьи — вы проверите перед применением."
                value={text} onChange={e => setText(e.target.value)} />
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button className="btn btn-primary btn-sm" onClick={() => send('bad', text, true)} disabled={busy || !text.trim()}>
          <Sparkles size={12} /> {busy ? 'AI составляет…' : 'Составить правило'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => setOpen(false)} disabled={busy}>Отмена</button>
      </div>
    </div>
  );
}

function TicketDetail({ api, ticketId, onBack }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => setData(await api(`/tickets/${ticketId}`)), [api, ticketId]);
  useEffect(() => { load(); }, [load]);

  const analyze = async () => {
    setBusy(true); setMsg('');
    const r = await api(`/tickets/${ticketId}/analyze`, { method: 'POST' });
    setMsg(r.ok ? `Разбор готов, уроков: ${r.lesson_ids.length}` : (r.error || 'Ошибка'));
    setBusy(false); load();
  };
  const feedbackDone = text => { setMsg(text); load(); };

  if (!data) return <div className="empty-state"><div className="loading-spinner" /></div>;
  const t = data.ticket;
  const analysis = data.analyses[0];

  return (
    <div>
      <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: 10 }}><ArrowLeft size={14} /> К списку</button>

      <div className="card">
        <div className="card-header" style={{ justifyContent: 'space-between' }}>
          <span className="card-title">{t.client_name || `ID ${t.client_id}`}</span>
          <span className="badge badge-info">{OUTCOME_LABELS[t.outcome] || t.outcome}</span>
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          @{t.client_username || '—'} · создан {formatDate(t.created_at)} · закрыт {formatDate(t.closed_at)} ({t.closed_by || '—'})
          {t.rating !== null && t.rating !== undefined && <> · оценка {t.rating ? '👍' : '👎'}</>}
        </div>
        {t.escalation_reason && <div className="ticket-reason" style={{ marginTop: 8 }}><b>Эскалация:</b> {t.escalation_reason}</div>}
      </div>

      <div className="card">
        <div className="card-header" style={{ justifyContent: 'space-between' }}>
          <span className="card-title"><Brain size={16} style={{ marginRight: 6 }} />Разбор AI</span>
          <button className="btn btn-primary btn-sm" onClick={analyze} disabled={busy}>
            <Sparkles size={14} /> {busy ? 'Анализирую…' : analysis ? 'Разобрать заново' : 'Разобрать'}
          </button>
        </div>
        {msg && <div className="alert alert-info">{msg}</div>}
        {analysis ? (
          <div style={{ fontSize: '0.84rem' }}>
            <p style={{ marginTop: 0 }}>{analysis.summary}</p>
            <div className="data-row"><span className="data-label">Категория</span><span className="data-value">{analysis.category}</span></div>
            <div className="data-row"><span className="data-label">Качество AI</span><span className="data-value">{analysis.ai_quality ? `${analysis.ai_quality} / 5` : '—'}</span></div>
            <div className="data-row"><span className="data-label">Настроение клиента</span><span className="data-value">{analysis.client_sentiment}</span></div>
            {analysis.ai_mistakes?.length > 0 && (
              <div style={{ marginTop: 8 }}><b>Ошибки AI:</b><ul style={{ margin: '4px 0', paddingLeft: 18 }}>{analysis.ai_mistakes.map((m, i) => <li key={i}>{m}</li>)}</ul></div>
            )}
            {analysis.manager_insight && <div style={{ marginTop: 6 }}><b>Что сделал менеджер:</b> {analysis.manager_insight}</div>}
            {data.lessons.length > 0 && (
              <div style={{ marginTop: 8 }}><b>Предложенные уроки:</b>
                {data.lessons.map(l => <div key={l.id} style={{ marginTop: 4 }}>• {l.title} <span className={`badge badge-${LESSON_STATUS[l.status]?.badge}`}>{LESSON_STATUS[l.status]?.label}</span></div>)}
              </div>
            )}
          </div>
        ) : <div className="empty-text">Тикет ещё не разобран.</div>}
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Диалог</span></div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 8 }}>Оценивайте ответы AI. «Плохо» → опишите своими словами, AI предложит правило или правку статьи.</div>
        <div className="messages-container">
          {t.history.map((m, i) => m.role === 'tool' ? (
            <div key={i} style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '4px 0' }}>
              🔧 {m.tool_name} {m.success === false ? '⚠️' : '✓'} — {m.result_summary?.slice(0, 120)}
            </div>
          ) : (
            <div key={i} className={`chat-message ${m.role}`} style={{ flexWrap: 'wrap' }}>
              <div className="chat-message-header">
                <span className="chat-message-role">{m.role === 'user' ? '👤 Клиент' : m.role === 'manager' ? `👨‍💼 ${m.name || 'Менеджер'}` : '🤖 AI'}</span>
                {m.timestamp && <span className="chat-message-time">{formatDate(m.timestamp)}</span>}
              </div>
              <div className="chat-message-text" style={{ whiteSpace: 'pre-wrap' }}>{stripHtml(m.content)}</div>
              {m.role === 'ai' && (m.admin_feedback ? (
                <div style={{ fontSize: '0.72rem', marginTop: 4, color: 'var(--text-secondary)' }}>
                  Оценка: {m.admin_feedback.verdict === 'good' ? '👍' : '👎'}{m.admin_feedback.correction ? ` — ${m.admin_feedback.correction}` : ''}
                </div>
              ) : <FeedbackBox api={api} ticketId={ticketId} index={i} onDone={feedbackDone} />)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TicketsTab({ api }) {
  const [filter, setFilter] = useState('no');
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => setData(await api(`/tickets?status=${filter === 'all' ? 'all' : 'closed'}&analyzed=${filter === 'all' ? '' : filter}`)), [api, filter]);
  useEffect(() => { if (!openId) load(); }, [load, openId]);

  if (openId) return <TicketDetail api={api} ticketId={openId} onBack={() => setOpenId(null)} />;

  return (
    <div>
      <div className="filter-tabs" style={{ marginBottom: 12 }}>
        <button className={`filter-tab ${filter === 'no' ? 'active' : ''}`} onClick={() => setFilter('no')}>Не разобраны</button>
        <button className={`filter-tab ${filter === 'yes' ? 'active' : ''}`} onClick={() => setFilter('yes')}>Разобраны</button>
        <button className={`filter-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>Все тикеты</button>
      </div>
      {!data ? <div className="empty-state"><div className="loading-spinner" /></div> :
        data.tickets.length === 0 ? <div className="empty-state"><div className="empty-title">Тикетов нет</div><div className="empty-text">История копится с момента обновления — закрытые тикеты больше не удаляются.</div></div> :
          data.tickets.map(t => (
            <div key={t.id} className="ticket-card" onClick={() => setOpenId(t.id)} style={{ cursor: 'pointer' }}>
              <div className="ticket-header">
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{t.client_name || `ID ${t.client_id}`}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{stripHtml(t.preview) || '—'}</div>
                </div>
                <div style={{ textAlign: 'right', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                  <div>{OUTCOME_LABELS[t.outcome] || t.outcome}</div>
                  <div>{formatDate(t.closed_at || t.created_at)} · {t.messages} сообщ.{t.rating !== null && t.rating !== undefined ? ` · ${t.rating ? '👍' : '👎'}` : ''}</div>
                  {t.analysis_status === 'done' && <span className="badge badge-success">разобран</span>}
                  {t.analysis_status === 'failed' && <span className="badge badge-danger">ошибка разбора</span>}
                </div>
              </div>
            </div>
          ))}
    </div>
  );
}

function LessonCard({ lesson, api, onChange }) {
  const [edit, setEdit] = useState(false);
  const [title, setTitle] = useState(lesson.title);
  const [content, setContent] = useState(lesson.content);
  const [category, setCategory] = useState(lesson.category);
  const st = LESSON_STATUS[lesson.status] || {};

  const setStatus = async status => { await api(`/lessons/${lesson.id}`, { method: 'PUT', body: JSON.stringify({ status }) }); onChange(); };
  const save = async () => { await api(`/lessons/${lesson.id}`, { method: 'PUT', body: JSON.stringify({ title, content, category }) }); setEdit(false); onChange(); };
  const remove = async () => { if (window.confirm('Удалить урок?')) { await api(`/lessons/${lesson.id}`, { method: 'DELETE' }); onChange(); } };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{edit ? <input className="input" value={title} onChange={e => setTitle(e.target.value)} /> : lesson.title}</div>
        <span className={`badge badge-${st.badge}`}>{st.label}</span>
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '4px 0 8px' }}>
        {CATEGORY_LABELS[lesson.category] || 'Общие'} · {TYPE_LABELS[lesson.type]} · уверенность {Math.round((lesson.confidence || 0) * 100)}% · встречался {lesson.occurrences}×
        {lesson.auto_applied && ' · применён автоматически'}{lesson.created_by !== 'ai' && ' · создан админом'}
      </div>
      {edit ? <>
        <CategorySelect value={category} onChange={setCategory} style={{ marginBottom: 6 }} />
        <textarea className="input" rows={5} value={content} onChange={e => setContent(e.target.value)} />
      </>
        : <div style={{ fontSize: '0.84rem', whiteSpace: 'pre-wrap' }}>{lesson.content}</div>}
      {lesson.reason && !edit && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 6 }}><i>Почему: {lesson.reason}</i></div>}
      {lesson.reject_reason && <div style={{ fontSize: '0.75rem', color: 'var(--warning)', marginTop: 4 }}>{lesson.reject_reason}</div>}
      {lesson.merged_from?.length > 0 && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>Объединено уроков: {lesson.merged_from.length + 1}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        {edit ? (<>
          <button className="btn btn-primary btn-sm" onClick={save}><Save size={12} /> Сохранить</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setEdit(false)}>Отмена</button>
        </>) : (<>
          {lesson.status === 'merged' && <button className="btn btn-secondary btn-sm" onClick={() => setStatus('pending')}>Вернуть на проверку</button>}
          {lesson.status !== 'approved' && lesson.status !== 'merged' && <button className="btn btn-primary btn-sm" onClick={() => setStatus('approved')}><Check size={12} /> Применить</button>}
          {lesson.status !== 'rejected' && lesson.status !== 'merged' && <button className="btn btn-secondary btn-sm" onClick={() => setStatus('rejected')}><X size={12} /> {lesson.status === 'approved' ? 'Отозвать' : 'Отклонить'}</button>}
          <button className="btn btn-secondary btn-sm" onClick={() => setEdit(true)}>Изменить</button>
          <button className="btn btn-icon btn-secondary btn-sm" onClick={remove}><Trash2 size={12} /></button>
        </>)}
      </div>
    </div>
  );
}

function LessonsTab({ api }) {
  const [status, setStatus] = useState('pending');
  const [category, setCategory] = useState('');
  const [lessons, setLessons] = useState(null);
  const [counts, setCounts] = useState({});
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ type: 'prompt_rule', category: '', title: '', content: '' });

  const load = useCallback(async () => {
    const r = await api(`/lessons?status=${status}&category=${encodeURIComponent(category)}`);
    setLessons(r.lessons); setCounts(r.category_counts || {});
  }, [api, status, category]);
  useEffect(() => { load(); }, [load]);

  const [consolidating, setConsolidating] = useState(false);
  const [consMsg, setConsMsg] = useState('');
  const consolidate = async () => {
    setConsolidating(true); setConsMsg('');
    const r = await api('/lessons/consolidate', { method: 'POST' });
    setConsMsg(r.ok ? `Объединено групп: ${r.groups} (уроков влито: ${r.merged}), отклонено как дубли ядра: ${r.redundant}` : (r.error || 'Ошибка'));
    setConsolidating(false); load();
  };

  const create = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    await api('/lessons', { method: 'POST', body: JSON.stringify(form) });
    setForm({ type: 'prompt_rule', category: '', title: '', content: '' }); setCreating(false); setStatus('approved'); load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div className="filter-tabs">
          {Object.entries(LESSON_STATUS).map(([k, v]) => (
            <button key={k} className={`filter-tab ${status === k ? 'active' : ''}`} onClick={() => setStatus(k)}>{v.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-secondary btn-sm" onClick={consolidate} disabled={consolidating}>
            <Sparkles size={14} /> {consolidating ? 'Объединяю…' : 'Объединить похожие'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setCreating(!creating)}><Plus size={14} /> Свой урок</button>
        </div>
      </div>

      <div className="filter-tabs" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
        <button className={`filter-tab ${category === '' ? 'active' : ''}`} onClick={() => setCategory('')}>
          Все темы {Object.values(counts).reduce((a, b) => a + b, 0) || ''}
        </button>
        {CATEGORIES.map(([id, label]) => (
          <button key={id} className={`filter-tab ${category === id ? 'active' : ''}`} onClick={() => setCategory(id)}>
            {label} {counts[id] || ''}
          </button>
        ))}
      </div>

      {consMsg && <div className="alert alert-info">{consMsg}</div>}

      {creating && (
        <div className="card">
          <div className="input-group">
            <label className="input-label">Тип</label>
            <select className="select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
              <option value="prompt_rule">Правило для AI (добавится в промпт)</option>
              <option value="kb_article">Статья базы знаний</option>
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Тема</label>
            <CategorySelect value={form.category} onChange={c => setForm({ ...form, category: c })} />
          </div>
          <div className="input-group"><label className="input-label">Заголовок</label>
            <input className="input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
          <div className="input-group"><label className="input-label">Содержание</label>
            <textarea className="input" rows={5} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} /></div>
          <button className="btn btn-primary btn-sm" onClick={create}><Check size={14} /> Создать и применить</button>
        </div>
      )}

      {!lessons ? <div className="empty-state"><div className="loading-spinner" /></div> :
        lessons.length === 0 ? <div className="empty-state"><div className="empty-title">Пусто</div><div className="empty-text">AI предложит уроки после разбора закрытых тикетов.</div></div> :
          lessons.map(l => <LessonCard key={l.id} lesson={l} api={api} onChange={load} />)}
    </div>
  );
}

function ReportsTab({ api }) {
  const [reports, setReports] = useState(null);
  useEffect(() => { api('/reports').then(r => setReports(r.reports)); }, [api]);
  if (!reports) return <div className="empty-state"><div className="loading-spinner" /></div>;
  if (!reports.length) return <div className="empty-state"><div className="empty-title">Отчётов пока нет</div><div className="empty-text">Сводный анализ запускается раз в сутки, когда накопится 5+ разборов, или вручную на вкладке «Обзор».</div></div>;
  return reports.map(r => (
    <div key={r.id} className="card">
      <div className="card-header" style={{ justifyContent: 'space-between' }}>
        <span className="card-title">{formatDate(r.created_at)}</span>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{r.analyses_count} разборов · {r.by === 'auto' ? 'авто' : 'вручную'} · уроков {r.lesson_ids?.length || 0}</span>
      </div>
      <div style={{ fontSize: '0.84rem', whiteSpace: 'pre-wrap' }}>{r.report}</div>
    </div>
  ));
}

function SettingsTab({ api }) {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState('');
  useEffect(() => { api('/settings').then(setS); }, [api]);

  const save = async patch => {
    const r = await api('/settings', { method: 'PUT', body: JSON.stringify(patch) });
    if (r.ok) { setS(r); setSaved('Сохранено'); setTimeout(() => setSaved(''), 1500); }
  };
  if (!s) return <div className="empty-state"><div className="loading-spinner" /></div>;

  const Row = ({ k, title, hint }) => (
    <div className="data-row" style={{ alignItems: 'center' }}>
      <div><div style={{ fontSize: '0.86rem' }}>{title}</div><div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{hint}</div></div>
      <div className={`toggle ${s[k] ? 'on' : ''}`} onClick={() => save({ [k]: !s[k] })}><div className="toggle-knob" /></div>
    </div>
  );

  return (
    <div className="card">
      <div className="card-header"><span className="card-title">Режим самообучения</span>{saved && <span className="badge badge-success">{saved}</span>}</div>
      <Row k="learning_auto_analyze" title="AI сам разбирает закрытые тикеты" hint="Каждые 10 минут, до 3 тикетов за раз, через активного AI-провайдера" />
      <Row k="learning_auto_insights" title="Ежедневный сводный анализ" hint="Раз в сутки, если накопилось 5+ новых разборов" />
      <Row k="learning_auto_apply" title="Применять уроки без одобрения" hint="Только уроки AI с высокой уверенностью, повторившиеся в нескольких тикетах" />
      {s.learning_auto_apply && (
        <div className="alert alert-info" style={{ marginTop: 10 }}>
          Уроки строятся по сообщениям клиентов. Автоприменение ограничено порогами ниже, но периодически просматривайте вкладку «Уроки → Применён».
        </div>
      )}
      <div className="input-group" style={{ marginTop: 12 }}>
        <label className="input-label">Мин. уверенность для автоприменения (0–1)</label>
        <input className="input" type="number" step="0.05" min="0" max="1" defaultValue={s.learning_auto_apply_confidence}
               onBlur={e => save({ learning_auto_apply_confidence: parseFloat(e.target.value) })} />
      </div>
      <div className="input-group">
        <label className="input-label">Мин. число тикетов, где встретился урок</label>
        <input className="input" type="number" min="1" defaultValue={s.learning_auto_apply_min_occurrences}
               onBlur={e => save({ learning_auto_apply_min_occurrences: parseInt(e.target.value, 10) })} />
      </div>
    </div>
  );
}

const TABS = [
  { id: 'overview', label: 'Обзор' },
  { id: 'tickets', label: 'Тикеты' },
  { id: 'lessons', label: 'Уроки' },
  { id: 'reports', label: 'Отчёты' },
  { id: 'settings', label: 'Режим' },
];

export default function LearningPage({ auth }) {
  const [tab, setTab] = useState('overview');
  const api = useApi(auth);
  const content = useMemo(() => ({
    overview: <OverviewTab api={api} onOpenReports={() => setTab('reports')} />,
    tickets: <TicketsTab api={api} />,
    lessons: <LessonsTab api={api} />,
    reports: <ReportsTab api={api} />,
    settings: <SettingsTab api={api} />,
  }), [api]);

  return (
    <div data-testid="learning-page">
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header"><span className="card-title"><BarChart3 size={16} style={{ marginRight: 6 }} />Аналитика и обучение AI</span></div>
        <div className="filter-tabs">
          {TABS.map(t => (
            <button key={t.id} className={`filter-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </div>
      </div>
      {content[tab]}
    </div>
  );
}
